<template>
  <div class="km-home">
    <!-- ===== TOP BAR ===== -->
    <header class="kh-top">
      <div class="kh-top__inner">
        <div class="kh-brand">
          <svg class="kh-brand__icon" viewBox="0 0 40 40" fill="none">
            <path d="M20 34s-12-7.4-12-16a7 7 0 0 1 12-4.9A7 7 0 0 1 32 18c0 8.6-12 16-12 16z" fill="url(#khg)"/>
            <defs><linearGradient id="khg" x1="8" y1="8" x2="32" y2="34">
              <stop stop-color="#ff4d8d"/><stop offset="1" stop-color="#a020f0"/>
            </linearGradient></defs>
          </svg>
          <span class="kh-brand__name">KissMora</span>
        </div>

        <!-- Desktop-only nav -->
        <nav class="kh-nav">
          <a class="kh-nav__item active">Home</a>
          <a class="kh-nav__item" @click="$router.push('/Video')">Cam</a>
          <a class="kh-nav__item" @click="$router.push('/Lottery')">Lottery</a>
          <a class="kh-nav__item" @click="$router.push('/Mine')">Profile</a>
        </nav>

        <!-- Global NotificationBell component handles notifications top-right on every page -->
        <div class="kh-top__right"></div>
      </div>
    </header>

    <!-- ===== FEATURED BANNER ===== -->
    <KmBanners/>

    <!-- ===== NOTICE TICKER ===== -->
    <section v-if="notice" class="kh-notice">
      <span class="kh-notice__ico">📣</span>
      <div class="kh-notice__track">
        <div class="kh-notice__text">{{ notice }}</div>
      </div>
    </section>

    <!-- ===== FEATURED / POPULAR (horizontal scroll) ===== -->
    <section class="kh-section">
      <div class="kh-sec-head">
        <h3>✨ Featured</h3>
        <span class="kh-sec-head__sub">Handpicked highlights today</span>
      </div>
      <div class="kh-strip">
        <div v-if="loading" class="kh-strip__row">
          <div v-for="i in 6" :key="'s'+i" class="kh-chip kh-chip--skel"></div>
        </div>
        <div v-else-if="topPicks.length === 0" class="kh-empty kh-empty--sm">
          <div class="kh-empty__emoji">✨</div>
          <p>New picks arrive daily — check back soon.</p>
        </div>
        <div v-else class="kh-strip__row">
          <div v-for="p in topPicks" :key="p.id" class="kh-chip" @click="openProfile(p)">
            <div class="kh-chip__img" :style="{background: gradientFor(p.id)}">
              <img v-if="p.img" :src="p.img" :alt="p.name" @error="onImgErr($event)"/>
            </div>
            <div class="kh-chip__name">{{ p.name }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== RECOMMENDED (vertical list) ===== -->
    <section class="kh-section" v-if="loading || forYou.length">
      <div class="kh-sec-head">
        <h3>💘 Recommended for you</h3>
        <span class="kh-sec-head__sub">Fresh picks for you</span>
      </div>
      <div v-if="loading" class="kh-list">
        <div v-for="i in 3" :key="'fs'+i" class="kh-listcard kh-listcard--skel"></div>
      </div>
      <div v-else class="kh-list">
        <article v-for="p in forYou" :key="p.id" class="kh-listcard" @click="openProfile(p)">
          <div class="kh-listcard__img" :style="{background: gradientFor(p.id)}">
            <img v-if="p.img" :src="p.img" :alt="p.name" class="kh-listcard__photo" @error="onImgErr($event)"/>
            <div class="kh-listcard__shade"></div>
            <div class="kh-listcard__body">
              <div class="kh-listcard__name">
                {{ p.name }}
              </div>
              <div class="kh-listcard__loc" v-if="p.city">📍 {{ p.city }}</div>
            </div>
            <button class="kh-listcard__heart" :class="{liked: p._liked}"
                    @click.stop="toggleLike(p)" aria-label="Like">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M12 21s-7-4.35-9.5-9.14C1.04 9.13 2.4 5.5 5.6 5.5c1.88 0 3.38 1.05 4.4 2.57C11.02 6.55 12.52 5.5 14.4 5.5c3.2 0 4.56 3.63 3.1 6.36C19 16.65 12 21 12 21z"/>
              </svg>
            </button>
          </div>
        </article>
      </div>
    </section>

    <!-- Bottom spacer for the tab bar -->
    <div class="kh-spacer"></div>
  </div>
</template>

<script>
import KmBanners from '../../components/KmBanners.vue';

// KissMora Home — uses the real backend endpoints:
//   sys_config  → banners + notice
//   xuanfeilist → Top picks + Recommended
// Real profile ids/images/order are preserved. Display names are shown in English
// (deterministic from the id) so the UI reads cleanly in an English-only app.
const ENGLISH_NAMES = [
  'Sophia','Emma','Aiko','Maya','Noelle','Priya','Lila','Zoe','Chloe','Ava',
  'Ruby','Luna','Mia','Isla','Nina','Yuki','Sora','Elena','Mila','Aria',
  'Iris','Nova','Hazel','Olivia','Grace','Jade','Sienna','Clara','Lyra','Rhea'
];
function isAsciiish(s){ return /^[\x20-\x7e\s]+$/.test(String(s || '')); }

// Rewrite image URLs so they actually load.
// Backend stores URLs like "http://localhost/xuanfei/..." (no port), or sometimes a bare "/xuanfei/...".
// Our PHP server runs on :8000, so we rebase any /xuanfei/, /upload/, /uploads/ path to :8000.
function resolveImg(raw){
  const u = String(raw || '').trim();
  if(!u) return '';
  // Strip the localhost-no-port prefix → keep the pathname
  let path = u;
  const m = /^https?:\/\/localhost(?::\d+)?(\/.*)$/i.exec(u);
  if(m) path = m[1];
  // If it's a bare server path we should serve from the PHP backend
  if(/^\/(xuanfei|upload|uploads|base|img|static)\b/i.test(path)){
    // Prefer the API origin from the current axios base URL
    // Our dev setup: frontend 8081 → backend 8000. Same host, different port.
    return window.location.protocol + '//' + window.location.hostname + ':8000' + path;
  }
  // Already an absolute non-localhost URL → leave alone
  if(/^https?:\/\//i.test(u)) return u;
  // Otherwise treat as relative path on the backend
  if(/^\//.test(u)) return window.location.protocol + '//' + window.location.hostname + ':8000' + u;
  return u;
}
function englishNameFrom(id, raw){
  // If the raw name is already clean English, keep it.
  const trimmed = String(raw || '').trim();
  if(trimmed && isAsciiish(trimmed) && trimmed.length >= 2) return trimmed;
  // Otherwise pick a deterministic English name from the id hash
  const h = Math.abs(Number(id) || 0);
  return ENGLISH_NAMES[h % ENGLISH_NAMES.length];
}

export default {
  name: 'KmHome',
  components: { KmBanners },
  data(){
    return {
      notice: '',
      notifCount: 0,
      loading: true,
      topPicks: [],
      forYou: [],
      banners: [],
      heroOpts: {
        loop: true,
        autoplay: { delay: 4500, disableOnInteraction: false },
        speed: 600,
        pagination: { el: '.kh-hero .swiper-pagination', clickable: true }
      }
    }
  },
  created(){
    if(!localStorage.getItem('token')){ this.$router.replace('/Login'); return; }
    this.loadConfig();
    this.loadPicks();
  },
  methods: {
    gradientFor(id){
      // 6 distinct rose/pink/purple gradients cycled by id so placeholders don't all look identical
      const GRADIENTS = [
        'linear-gradient(135deg, #ff6b9d 0%, #a020f0 100%)',
        'linear-gradient(135deg, #fda4af 0%, #ff4d8d 100%)',
        'linear-gradient(135deg, #c084fc 0%, #7209b7 100%)',
        'linear-gradient(135deg, #f72585 0%, #a020f0 100%)',
        'linear-gradient(135deg, #ffb3d1 0%, #ff4d8d 100%)',
        'linear-gradient(135deg, #e0aaff 0%, #5a189a 100%)'
      ];
      const h = Math.abs(Number(id) || 0);
      return GRADIENTS[h % GRADIENTS.length];
    },
    onImgErr(e){
      // Hide broken <img> so the gradient background shows through
      if(e && e.target) e.target.style.display = 'none';
    },
    bannerShow(b){
      // Only show a banner overlay caption when the name is clean English.
      return b && b.name && isAsciiish(b.name);
    },
    bannerBg(b){
      const fallback = 'linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%)';
      const u = String((b && b.url) || '').trim();
      if(!u) return fallback;
      const url = /^https?:\/\//i.test(u) ? u : 'https://' + u.replace(/^\/+/, '');
      // Layer the real URL on top of the brand gradient — if the URL 404s, the gradient still looks classy.
      return 'url("' + url + '"), ' + fallback;
    },
    loadConfig(){
      this.$http({ method: 'get', url: 'sys_config' }).then(res => {
        const d = (res && res.data) || {};
        this.notice = d.notice || '';
        this.banners = Array.isArray(d.banners) ? d.banners : [];
      }).catch(() => {});
    },
    normalize(r){
      return {
        id: r.id,
        name: englishNameFrom(r.id, r.xuanfei_name),
        img: resolveImg(r.img_url),
        city: String(r.address || '').replace(/[^\x20-\x7e]/g, '').trim(),
        dianzan: r.dianzan,
        _liked: false
      };
    },
    loadPicks(){
      this.$http({ method: 'get', url: 'xuanfeilist', data: { id: 1, page: 1 } }).then(res => {
        const list = Array.isArray(res && res.data) ? res.data : [];
        const clean = list
          // Keep every entry; broken images get the gradient placeholder via normalizeImg.
          .filter(r => r && r.id)
          .map(r => this.normalize(r));
        this.topPicks = clean.slice(0, 10);
        this.forYou  = clean.slice(0, 6);
        this.loading = false;
      }).catch(() => {
        this.topPicks = []; this.forYou = [];
        this.loading = false;
      });
    },
    openProfile(p){
      if(p && p.id) this.$router.push({ path: '/detail', query: { id: p.id } });
    },
    toggleLike(p){
      this.$set(p, '_liked', !p._liked);
    },
    goNotifications(){ this.$toast('Notifications coming soon 🔔'); }
  }
}
</script>

<style scoped>
.km-home {
  min-height: 100vh;
  background:
    radial-gradient(800px 300px at -10% 0%, rgba(255,77,141,.06), transparent 60%),
    radial-gradient(700px 300px at 110% 10%, rgba(160,32,240,.06), transparent 60%),
    #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, "PingFang SC", sans-serif;
  color: #1a1033;
  padding-top: 56px;
}
.km-home * { box-sizing: border-box; }
button { font-family: inherit; }

/* ========== TOP BAR ========== */
.kh-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 40;
  background: rgba(255,255,255,.88);
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border-bottom: 1px solid rgba(26,16,51,.05);
}
.kh-top__inner {
  max-width: 1180px; margin: 0 auto;
  padding: 0 16px; height: 56px;
  display: flex; align-items: center; justify-content: space-between;
}
.kh-brand { display: flex; align-items: center; gap: 8px; }
.kh-brand__icon { width: 28px; height: 28px; }
.kh-brand__name {
  font-size: 20px; font-weight: 800; letter-spacing: -.02em;
  background: linear-gradient(90deg, #ff4d8d, #a020f0);
  -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
}
/* Desktop nav (hidden on mobile) */
.kh-nav { display: none; gap: 4px; flex: 1; justify-content: center; }
.kh-nav__item {
  padding: 9px 16px; border-radius: 10px; font-size: 14px; font-weight: 600;
  color: #4a3d66; cursor: pointer; transition: background .2s, color .2s;
}
.kh-nav__item:hover { background: rgba(255,77,141,.08); color: #1a1033; }
.kh-nav__item.active {
  background: linear-gradient(90deg, rgba(255,77,141,.12), rgba(160,32,240,.12));
  color: #ff4d8d;
}

.kh-top__right { display: flex; align-items: center; gap: 10px; }
.kh-top__bell {
  position: relative; width: 38px; height: 38px; border-radius: 50%;
  background: #fff; border: 1px solid rgba(26,16,51,.08);
  display: flex; align-items: center; justify-content: center; cursor: pointer;
  color: #1a1033; transition: border-color .2s;
}
.kh-top__bell:hover { border-color: #ff4d8d; }
.kh-top__me {
  width: 38px; height: 38px; border-radius: 50%; cursor: pointer;
  background: linear-gradient(135deg, #ff4d8d, #a020f0); color: #fff;
  border: none; display: none;
  align-items: center; justify-content: center;
  box-shadow: 0 6px 14px rgba(255,77,141,.35);
  transition: transform .15s, box-shadow .2s;
}
.kh-top__me:hover { transform: translateY(-1px); box-shadow: 0 10px 22px rgba(255,77,141,.5); }
.kh-top__badge {
  position: absolute; top: -2px; right: -2px;
  background: linear-gradient(135deg,#ff4d8d,#a020f0);
  color: #fff; font-size: 10px; font-weight: 700;
  border-radius: 10px; padding: 2px 6px; min-width: 18px; text-align: center;
  border: 2px solid #fff;
}

/* ========== HERO BANNER ========== */
.kh-hero { max-width: 1180px; margin: 0 auto; padding: 14px 16px 0; }
.kh-hero__swiper { border-radius: 18px; overflow: hidden; }
.kh-hero__slide {
  position: relative; aspect-ratio: 21 / 9; min-height: 140px;
  background-size: cover; background-position: center;
  background-color: #4a1a6e;
  background-image: linear-gradient(135deg, #ff4d8d 0%, #a020f0 100%); /* fallback if real image fails */
  cursor: pointer; border-radius: 18px;
  display: flex; align-items: flex-end;
}
.kh-hero__tint {
  position: absolute; inset: 0;
  background: linear-gradient(110deg, rgba(26,16,51,.65) 0%, rgba(74,26,110,.4) 60%, rgba(0,0,0,.1) 100%);
}
.kh-hero__body { position: relative; padding: 16px 18px 18px; color: #fff; max-width: 80%; }
.kh-hero__badge {
  display: inline-block; font-size: 11px; font-weight: 700;
  padding: 4px 10px; border-radius: 999px;
  background: rgba(255,255,255,.2); backdrop-filter: blur(8px);
  margin-bottom: 8px;
}
.kh-hero__title {
  font-size: 20px; font-weight: 800; margin: 0 0 4px;
  line-height: 1.2; text-shadow: 0 2px 10px rgba(0,0,0,.35);
}
.kh-hero__sub { font-size: 13px; opacity: .9; margin: 0; line-height: 1.4; }
.kh-hero :deep(.swiper-pagination-bullet),
.kh-hero /deep/ .swiper-pagination-bullet { background: rgba(255,255,255,.55); opacity: 1; width: 6px; height: 6px; }
.kh-hero :deep(.swiper-pagination-bullet-active),
.kh-hero /deep/ .swiper-pagination-bullet-active { background: #fff; width: 18px; border-radius: 3px; }

/* ========== NOTICE TICKER ========== */
.kh-notice {
  max-width: 1180px; margin: 12px auto 0;
  margin-left: 16px; margin-right: 16px;
  display: flex; align-items: center; gap: 10px;
  background: #fff; border: 1px solid rgba(255,77,141,.15);
  border-radius: 12px; padding: 9px 14px;
  box-shadow: 0 2px 6px rgba(26,16,51,.04);
}
.kh-notice__ico { font-size: 15px; flex-shrink: 0; }
.kh-notice__track { flex: 1; overflow: hidden; height: 18px; position: relative; }
.kh-notice__text {
  position: absolute; left: 0; white-space: nowrap;
  font-size: 13px; color: #1a1033; font-weight: 500;
  animation: kh-slide 22s linear infinite;
}
@keyframes kh-slide {
  0%   { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
}

/* ========== SECTION HEAD ========== */
.kh-section { max-width: 1180px; margin: 0 auto; padding: 18px 16px 0; }
.kh-sec-head {
  display: flex; align-items: baseline; justify-content: space-between;
  margin-bottom: 12px;
}
.kh-sec-head h3 { margin: 0; font-size: 16px; font-weight: 800; letter-spacing: -.01em; }
.kh-sec-head__sub { font-size: 12px; color: #8d7fa6; }
.kh-sec-head a {
  font-size: 13px; font-weight: 600; color: #ff4d8d; cursor: pointer;
  text-decoration: none;
}
.kh-sec-head a:hover { text-decoration: underline; }

/* ========== FEATURED STRIP (horizontal scroll chips) ========== */
.kh-strip { margin: 0 -16px; padding: 0 16px 4px; }
.kh-strip__row {
  display: flex; gap: 12px; overflow-x: auto; scrollbar-width: none;
  padding-bottom: 4px;
}
.kh-strip__row::-webkit-scrollbar { display: none; }
.kh-chip { flex-shrink: 0; width: 84px; cursor: pointer; text-align: center; }
.kh-chip__img {
  width: 84px; height: 84px; border-radius: 16px;
  position: relative; box-shadow: 0 4px 12px rgba(26,16,51,.08);
  border: 2px solid transparent; overflow: hidden;
  background-clip: padding-box;
  transition: transform .15s, box-shadow .2s;
  display: flex; align-items: center; justify-content: center;
}
.kh-chip__img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.kh-chip__img::after {
  /* subtle user silhouette visible when image is empty/broken */
  content: '👤'; font-size: 36px; color: rgba(255,255,255,.5);
  position: absolute; left: 50%; top: 50%; transform: translate(-50%,-50%); z-index: 0;
}
.kh-chip__img img { position: relative; z-index: 1; }
.kh-chip:hover .kh-chip__img { transform: translateY(-2px); box-shadow: 0 10px 20px rgba(160,32,240,.18); }
.kh-chip__dot {
  position: absolute; bottom: 6px; right: 6px;
  width: 10px; height: 10px; border-radius: 50%; background: #10b981;
  border: 2px solid #fff; box-shadow: 0 0 0 2px rgba(16,185,129,.3);
}
.kh-chip__name {
  font-size: 12px; font-weight: 600; color: #1a1033;
  margin-top: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kh-chip--skel {
  width: 84px; height: 84px; border-radius: 16px;
  background: linear-gradient(90deg, #faf3f8, #f0e6ee, #faf3f8);
  background-size: 200% 100%; animation: kh-shimmer 1.4s linear infinite;
}
@keyframes kh-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* ========== LIST (grid of profile cards) ========== */
.kh-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.kh-listcard {
  position: relative; border-radius: 18px; overflow: hidden; cursor: pointer;
  background: #faf3f8;
  box-shadow: 0 4px 14px rgba(26,16,51,.06);
  transition: transform .2s, box-shadow .25s;
  aspect-ratio: 3/4;
}
.kh-listcard:hover { transform: translateY(-3px); box-shadow: 0 14px 30px rgba(160,32,240,.15); }
.kh-listcard__img {
  position: absolute; inset: 0; overflow: hidden;
}
.kh-listcard__photo { width: 100%; height: 100%; object-fit: cover; display: block; position: relative; z-index: 1; }
.kh-listcard__img::after {
  content: '👤'; font-size: 80px; color: rgba(255,255,255,.55);
  position: absolute; left: 50%; top: 45%; transform: translate(-50%,-50%); z-index: 0;
}
.kh-listcard__shade {
  position: absolute; inset: 0;
  background: linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,.82) 100%);
}
.kh-listcard__verified {
  position: absolute; top: 10px; left: 10px; z-index: 2;
  width: 22px; height: 22px; border-radius: 50%;
  background: linear-gradient(135deg,#3b82f6,#2563eb);
  color: #fff; font-weight: 800; font-size: 10px;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #fff; box-shadow: 0 2px 6px rgba(59,130,246,.4);
}
.kh-listcard__body {
  position: absolute; bottom: 10px; left: 12px; right: 12px; color: #fff; z-index: 2;
}
.kh-listcard__name {
  font-size: 15px; font-weight: 800; letter-spacing: -.01em;
  text-shadow: 0 2px 8px rgba(0,0,0,.5);
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  line-height: 1.2;
}
.kh-listcard__age { font-weight: 500; opacity: .9; }
.kh-listcard__live {
  display: inline-flex; align-items: center; gap: 3px;
  background: rgba(16,185,129,.95); font-size: 9px; font-weight: 700;
  padding: 2px 6px; border-radius: 999px; text-shadow: none;
  letter-spacing: .03em;
}
.kh-listcard__live span {
  width: 4px; height: 4px; border-radius: 50%; background: #fff;
  box-shadow: 0 0 0 2px rgba(255,255,255,.35);
}
.kh-listcard__loc {
  font-size: 11px; margin-top: 3px; opacity: .95;
  text-shadow: 0 2px 6px rgba(0,0,0,.5);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.kh-listcard__heart {
  position: absolute; top: 10px; right: 10px; z-index: 3;
  width: 34px; height: 34px; border-radius: 50%; border: none; cursor: pointer;
  background: rgba(255,255,255,.95); color: #ff4d8d;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,.15);
  transition: transform .2s, background .2s;
  -webkit-tap-highlight-color: transparent;
}
.kh-listcard__heart svg { width: 15px; height: 15px; }
.kh-listcard__heart:hover { transform: scale(1.1); }
.kh-listcard__heart.liked {
  background: linear-gradient(135deg,#ff4d8d,#a020f0); color: #fff;
  animation: kh-pop .45s ease;
}
@keyframes kh-pop {
  0%   { transform: scale(1); }
  40%  { transform: scale(1.35); }
  100% { transform: scale(1); }
}
.kh-listcard--skel {
  aspect-ratio: 3/4; border-radius: 18px;
  background: linear-gradient(90deg, #faf3f8, #f0e6ee, #faf3f8);
  background-size: 200% 100%; animation: kh-shimmer 1.4s linear infinite;
}

.kh-spacer { height: 100px; /* clear bottom tabbar */ }

/* Empty state */
.kh-empty {
  text-align: center; padding: 40px 16px;
  background: linear-gradient(180deg, #fff 0%, #fff6fa 100%);
  border: 1px dashed rgba(255,77,141,.25);
  border-radius: 18px;
}
.kh-empty__emoji { font-size: 40px; margin-bottom: 8px; }
.kh-empty p { margin: 0; font-size: 13px; color: #8273a0; }
.kh-empty--sm { padding: 24px 16px; }
.kh-empty--sm .kh-empty__emoji { font-size: 32px; margin-bottom: 4px; }

/* ========== TABLET / DESKTOP ========== */
@media (min-width: 720px) {
  .kh-top__inner { padding: 0 24px; height: 66px; }
  .km-home { padding-top: 66px; }
  .kh-nav { display: flex; }
  .kh-top__me { display: flex; }

  .kh-hero, .kh-section { padding-left: 24px; padding-right: 24px; }
  .kh-hero__slide { min-height: 220px; border-radius: 22px; }
  .kh-hero__title { font-size: 26px; }
  .kh-hero__sub   { font-size: 14px; }
  .kh-notice { margin-left: 24px; margin-right: 24px; padding: 11px 18px; }
  .kh-notice__text { font-size: 14px; }
  .kh-sec-head h3 { font-size: 18px; }
  .kh-strip { margin: 0; padding: 0; }
  .kh-chip { width: 110px; }
  .kh-chip__img { width: 110px; height: 110px; border-radius: 18px; }
  .kh-list { grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .kh-listcard__name { font-size: 17px; }
  .kh-listcard__loc  { font-size: 12px; }
  .kh-listcard__heart { width: 38px; height: 38px; }
  .kh-spacer { height: 40px; }
}

@media (min-width: 1024px) {
  .kh-list { grid-template-columns: repeat(4, 1fr); }
  .kh-hero__slide { min-height: 280px; }
  .kh-hero__title { font-size: 34px; }
  .kh-chip { width: 110px; }
  .kh-chip__img { width: 110px; height: 110px; }
}

/* ========== VERY SMALL PHONES ========== */
@media (max-width: 360px) {
  .kh-top__inner { padding: 0 12px; }
  .kh-brand__name { font-size: 18px; }
  .kh-hero, .kh-section { padding-left: 12px; padding-right: 12px; }
  .kh-hero__slide { min-height: 120px; }
  .kh-hero__title { font-size: 17px; }
  .kh-hero__sub   { font-size: 12px; }
  .kh-notice { margin-left: 12px; margin-right: 12px; padding: 8px 12px; }
  .kh-chip { width: 76px; }
  .kh-chip__img { width: 76px; height: 76px; }
  .kh-listcard__name { font-size: 16px; }
  .kh-listcard__heart { width: 36px; height: 36px; }
}
</style>
